import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { Button as SemanticButton, Form, Input } from "semantic-ui-react";
import {
  FurtherInstructionsGpt3,
  PromptConfigOverwrite,
  GptModel,
  GptProviders,
  PARAMETER_CONFIGS,
  StringParameter,
  ParameterType,
  NumberParameter,
  WebSearchConfig,
  WebSearchEngine,
  WebSearchContextSize,
  WebSearchDomainMode,
} from "./types";
import { Text } from "../../components/Text";
import debounce from "lodash/debounce";
import { Button, Icon } from "../../components/tailwind";
import {
  PromptBooleanField,
  PromptSliderField,
  PromptSelectField,
} from "./PromptFormInputs/InputFields";

export const WEB_SEARCH_ENGINE_OPTIONS: {
  value: WebSearchEngine;
  text: string;
}[] = [
  { value: "native", text: "Native" },
  { value: "exa", text: "Exa" },
  { value: "firecrawl", text: "Firecrawl" },
  { value: "parallel", text: "Parallel" },
];

export const WEB_SEARCH_CONTEXT_SIZE_OPTIONS: {
  value: WebSearchContextSize;
  text: string;
}[] = [
  { value: "low", text: "Low" },
  { value: "medium", text: "Medium" },
  { value: "high", text: "High" },
];

export const WEB_SEARCH_DOMAIN_MODE_OPTIONS: {
  value: WebSearchDomainMode;
  text: string;
}[] = [
  { value: "exclude", text: "Exclude" },
  { value: "include", text: "Include" },
];

export const WEB_SEARCH_DEFAULT_ENGINE: WebSearchEngine = "exa";
export const WEB_SEARCH_DEFAULT_CONTEXT_SIZE: WebSearchContextSize = "low";
export const WEB_SEARCH_DEFAULT_DOMAIN_MODE: WebSearchDomainMode = "exclude";
export const WEB_SEARCH_DEFAULT_SEARCH_PROMPT = "Some relevant web results:";
export const WEB_SEARCH_MAX_RESULTS_LIMIT = 5;

export type Props = {
  selectedModelName: string;
  selectedModel: GptModel;
  modelsConfig?: { [key: string]: any };
  advancedParameters: {
    further_instructions: string[] | FurtherInstructionsGpt3[];
    config_overwrite: PromptConfigOverwrite;
  };
  collectAdvancedParameters: (
    parameters: Partial<{
      further_instructions: string[] | FurtherInstructionsGpt3[];
      config_overwrite: PromptConfigOverwrite;
    }>
  ) => void;
  showUseWebSearch: boolean;
  useWebSearch: boolean;
  setUseWebSearch: (value: boolean) => void;
  webSearchConfig: WebSearchConfig;
  setWebSearchConfig: (config: WebSearchConfig) => void;
};

export const PromptFormModelSettings: React.FC<Props> = ({
  selectedModelName,
  selectedModel,
  modelsConfig,
  advancedParameters,
  collectAdvancedParameters,
  showUseWebSearch,
  useWebSearch,
  setUseWebSearch,
  webSearchConfig,
  setWebSearchConfig,
}) => {
  const engine: WebSearchEngine =
    webSearchConfig.engine || WEB_SEARCH_DEFAULT_ENGINE;
  const isExaEngine = engine === "exa";
  const searchPrompt =
    webSearchConfig.search_prompt ?? WEB_SEARCH_DEFAULT_SEARCH_PROMPT;
  const maxResults = webSearchConfig.max_results || 1;
  const contextSize: WebSearchContextSize =
    webSearchConfig.search_context_size || WEB_SEARCH_DEFAULT_CONTEXT_SIZE;
  const domainMode: WebSearchDomainMode =
    webSearchConfig.domain_mode || WEB_SEARCH_DEFAULT_DOMAIN_MODE;
  const domains = webSearchConfig.domains ?? [];

  const updateWebSearchConfig = useCallback(
    (partial: Partial<WebSearchConfig>): void => {
      setWebSearchConfig({ ...webSearchConfig, ...partial });
    },
    [webSearchConfig, setWebSearchConfig]
  );

  const updateDomain = (index: number, value: string): void => {
    const next = [...domains];
    next[index] = value;
    updateWebSearchConfig({ domains: next });
  };

  const addDomain = (): void => {
    updateWebSearchConfig({ domains: [...domains, ""] });
  };

  const removeDomain = (index: number): void => {
    const next = domains.filter((_, i) => i !== index);
    updateWebSearchConfig({ domains: next });
  };

  const currentConfig = useMemo(() => {
    const overwriteConfig =
      advancedParameters.config_overwrite?.[selectedModelName];
    return overwriteConfig || modelsConfig;
  }, [advancedParameters.config_overwrite, selectedModelName, modelsConfig]);

  const [textareaValue, setTextareaValue] = useState<string>("");
  const isEditingRef = useRef(false);
  const lastSyncedModelRef = useRef<string | undefined>(undefined);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      selectedModelName !== lastSyncedModelRef.current ||
      !isEditingRef.current
    ) {
      const configString = JSON.stringify(currentConfig, null, 4);
      setTextareaValue(configString);
      lastSyncedModelRef.current = selectedModelName;
      isEditingRef.current = false;
    }
  }, [currentConfig, selectedModelName]);

  const supportedParameters = selectedModel?.supported_parameters || [];

  const getReasoningValue = (reasoningValue: any): string | undefined => {
    if (!reasoningValue) return undefined;
    if (typeof reasoningValue === "object" && reasoningValue.effort) {
      return reasoningValue.effort;
    }
    return undefined;
  };

  const updateParameterValue = useCallback(
    (paramName: string, value: number | string) => {
      if (!isMountedRef.current) {
        return;
      }
      const baseConfig =
        advancedParameters.config_overwrite?.[selectedModelName] || {};
      const copy = { ...advancedParameters.config_overwrite };

      // If value is "default", remove the parameter from config
      if (value === "default") {
        const updatedConfig = { ...baseConfig };
        delete updatedConfig[paramName];
        // If config is now empty, remove the entire model entry
        if (Object.keys(updatedConfig).length === 0) {
          delete copy[selectedModelName];
        } else {
          copy[selectedModelName] = updatedConfig;
        }
      } else {
        let transformedValue: number | string | { effort: string } = value;
        if (paramName === "reasoning" && typeof value === "string") {
          transformedValue = { effort: value };
        }

        const updatedConfig = {
          ...baseConfig,
          [paramName]: transformedValue,
        };
        copy[selectedModelName] = updatedConfig;
      }
      collectAdvancedParameters({ config_overwrite: copy });
    },
    [
      advancedParameters.config_overwrite,
      selectedModelName,
      collectAdvancedParameters,
    ]
  );

  const resetToDefaults = useCallback(() => {
    const copy = { ...advancedParameters.config_overwrite };
    copy[selectedModelName] = {};
    collectAdvancedParameters({ config_overwrite: copy });
    setUseWebSearch(false);
  }, [selectedModelName, collectAdvancedParameters, setUseWebSearch]);

  const [JSONParseError, setJSONParseError] = useState<string | null>(null);
  const renderBody = (): React.ReactNode => {
    // Separate slider configs from select configs
    const sliderConfigs = supportedParameters
      .filter((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        return (
          config &&
          ((config as NumberParameter).type === ParameterType.FLOAT ||
            (config as NumberParameter).type === ParameterType.INT)
        );
      })
      .map((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        // Type guard: we know config exists and has min/max/step from the filter
        if (
          !config ||
          (config as NumberParameter).type === ParameterType.STRING
        ) {
          return null;
        }
        // Provide default value to prevent controlled/uncontrolled input warning
        const paramValue = currentConfig?.[paramName];
        const defaultValue = paramValue !== undefined ? paramValue : 0;
        return {
          name: paramName,
          config,
          value: defaultValue,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    const selectConfigs = supportedParameters
      .filter((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        return (
          config &&
          config.type === ParameterType.STRING &&
          (config as StringParameter).options
        );
      })
      .map((paramName) => {
        const config = PARAMETER_CONFIGS[paramName];
        // Type guard: we know config exists and has options from the filter
        if (
          !config ||
          (config as StringParameter).type !== ParameterType.STRING
        ) {
          return null;
        }

        // Extract reasoning value from {"effort": "low"} format
        const overwriteConfig =
          advancedParameters.config_overwrite?.[selectedModelName];
        const hasOverwrite = !!overwriteConfig?.[paramName];

        // If no overwrite, use "default" as the value
        let value: string;
        if (!hasOverwrite) {
          value = "default";
        } else if (paramName === "reasoning") {
          const extractedValue = getReasoningValue(currentConfig?.[paramName]);
          value = extractedValue || (config as StringParameter).options[0];
        } else {
          value =
            currentConfig?.[paramName] ||
            (config as StringParameter).options[0];
        }

        return {
          name: paramName,
          config,
          value,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    return (
      <span data-testid="prompt-form-model-settings-inputs">
        {GptProviders.OPENROUTER === selectedModel?.provider ? (
          <Form.Field>
            {selectConfigs.map(({ name, config, value }) => (
              <PromptSelectField
                key={name}
                testId={`copy-assistant-template-form-${name}`}
                caption={config.caption || ""}
                label={config.label}
                placeholder={`Select ${config.label}`}
                dropdownOptions={[
                  {
                    key: "default",
                    value: "default",
                    text: "Default",
                  },
                  ...(config as StringParameter).options.map((option) => ({
                    key: option,
                    value: option,
                    text: option.charAt(0).toUpperCase() + option.slice(1),
                  })),
                ]}
                value={value}
                setValue={(newValue: string) =>
                  updateParameterValue(name, newValue)
                }
              />
            ))}
            {showUseWebSearch && (
              <>
                <PromptBooleanField
                  testId="copy-assistant-template-form-use-web-search"
                  caption="Give the AI access to browse the web."
                  label={"Web Search"}
                  note="Please note that websearch cost additional credits."
                  value={useWebSearch}
                  setValue={setUseWebSearch}
                />
                {useWebSearch && (
                  <Form.Field data-testid="copy-assistant-template-form-web-search-settings">
                    <PromptSelectField
                      testId="copy-assistant-template-form-web-search-engine"
                      caption="Search engine used for web search."
                      label="Engine"
                      placeholder="Select engine"
                      dropdownOptions={WEB_SEARCH_ENGINE_OPTIONS.map((opt) => ({
                        key: opt.value,
                        value: opt.value,
                        text: opt.text,
                      }))}
                      value={engine}
                      setValue={(value): void =>
                        updateWebSearchConfig({
                          engine: value as WebSearchEngine,
                        })
                      }
                      clearable={false}
                    />
                    <Form.Field>
                      <label>Search prompt</label>
                      <Text color="grey" className="descriptive-helper-text">
                        Label injected before web search results.
                      </Text>
                      <Input
                        data-testid="copy-assistant-template-form-web-search-prompt"
                        placeholder={WEB_SEARCH_DEFAULT_SEARCH_PROMPT}
                        value={searchPrompt}
                        onChange={({ target }): void =>
                          updateWebSearchConfig({ search_prompt: target.value })
                        }
                        maxLength={2000}
                      />
                    </Form.Field>
                    <Form.Field>
                      <label>Max results</label>
                      <Text color="grey" className="descriptive-helper-text">
                        Max number of web search results (1-
                        {WEB_SEARCH_MAX_RESULTS_LIMIT}).
                      </Text>
                      <Input
                        data-testid="copy-assistant-template-form-web-search-max-results"
                        type="number"
                        min={1}
                        max={WEB_SEARCH_MAX_RESULTS_LIMIT}
                        value={maxResults}
                        onChange={({ target }): void => {
                          const parsed = parseInt(target.value, 10);
                          updateWebSearchConfig({
                            max_results: Number.isFinite(parsed)
                              ? Math.max(
                                  1,
                                  Math.min(WEB_SEARCH_MAX_RESULTS_LIMIT, parsed)
                                )
                              : 1,
                          });
                        }}
                      />
                    </Form.Field>
                    {isExaEngine && (
                      <PromptSelectField
                        testId="copy-assistant-template-form-web-search-context-size"
                        caption="Controls characters per result."
                        label="Context size"
                        placeholder="Select context size"
                        dropdownOptions={WEB_SEARCH_CONTEXT_SIZE_OPTIONS.map(
                          (opt) => ({
                            key: opt.value,
                            value: opt.value,
                            text: opt.text,
                          })
                        )}
                        value={contextSize}
                        setValue={(value): void =>
                          updateWebSearchConfig({
                            search_context_size: value as WebSearchContextSize,
                          })
                        }
                        clearable={false}
                      />
                    )}
                    <PromptSelectField
                      testId="copy-assistant-template-form-web-search-domain-mode"
                      caption="Wildcards are supported (*.example.com). Some native providers do not support all filtering options."
                      label="Domain filtering"
                      placeholder="Select domain mode"
                      dropdownOptions={WEB_SEARCH_DOMAIN_MODE_OPTIONS.map(
                        (opt) => ({
                          key: opt.value,
                          value: opt.value,
                          text: opt.text,
                        })
                      )}
                      value={domainMode}
                      setValue={(value): void =>
                        updateWebSearchConfig({
                          domain_mode:
                            (value as WebSearchDomainMode) || "exclude",
                        })
                      }
                      clearable={false}
                    />
                    <Form.Field>
                      <label>Domains</label>
                      {domains.map((domain, index) => (
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          style={{
                            display: "flex",
                            gap: "0.5rem",
                            marginBottom: "0.5rem",
                          }}
                        >
                          <Input
                            data-testid={`copy-assistant-template-form-web-search-domain-${index}`}
                            placeholder="e.g. reddit.com"
                            value={domain}
                            fluid
                            style={{ flex: 1 }}
                            onChange={({ target }): void =>
                              updateDomain(index, target.value)
                            }
                          />
                          <SemanticButton
                            type="button"
                            basic
                            color="red"
                            size="mini"
                            icon="minus"
                            onClick={(): void => removeDomain(index)}
                            data-testid={`copy-assistant-template-form-web-search-domain-remove-${index}`}
                          />
                        </div>
                      ))}
                      <SemanticButton
                        type="button"
                        basic
                        color="red"
                        size="mini"
                        icon="add"
                        onClick={addDomain}
                        data-testid="copy-assistant-template-form-web-search-domain-add"
                      />
                    </Form.Field>
                  </Form.Field>
                )}
              </>
            )}
            {sliderConfigs.map(({ name, config, value }) => (
              <PromptSliderField
                key={name}
                testId={`copy-assistant-template-form-${name}`}
                caption={(config as NumberParameter).caption}
                label={(config as NumberParameter).label}
                min={(config as NumberParameter).min}
                max={(config as NumberParameter).max}
                step={(config as NumberParameter).step}
                value={value}
                setValue={(newValue: number) =>
                  updateParameterValue(name, newValue)
                }
              />
            ))}{" "}
            <div className="tw-mb-4">
              <Button
                type="button"
                data-testid="prompt-form-model-settings-reset-button"
                variant="secondary-alt"
                size="sm"
                icon
                onClick={resetToDefaults}
              >
                Reset to OpenRouter default
              </Button>
            </div>
          </Form.Field>
        ) : (
          <Form.Field>
            <p className="tw-mx-0 tw-flex tw-items-center tw-gap-1">
              <Icon
                name="warning"
                className="tw-text-lg tw-leading-[1] tw-text-yellow-500"
              />{" "}
              If this is not used correctly, it can break the prompt.
            </p>
            <label>Provider API Config</label>
            <Text color="grey" className="descriptive-helper-text">
              In a JSON format, you can override the default parameters for the
              provider API. Please refer to the provider API documentation for
              the available parameters.
            </Text>
            <textarea
              data-testid="prompt-form-model-settings-config-overwrite-input"
              value={textareaValue}
              onChange={({ target }): void => {
                isEditingRef.current = true;
                setTextareaValue(target.value);
                updateConfigOverwrite(target.value);
              }}
            />
          </Form.Field>
        )}
        {JSONParseError && (
          <Text
            color="red"
            className="descriptive-helper-text"
            testId="prompt-form-model-settings-config-overwrite-error"
          >
            {JSONParseError}
          </Text>
        )}
      </span>
    );
  };

  const updateConfigOverwrite = useCallback(
    debounce((value: string): void => {
      if (!isMountedRef.current) {
        return;
      }
      setJSONParseError(null);
      if (!value) {
        const copy = { ...advancedParameters.config_overwrite };
        if (copy[selectedModelName]) {
          delete copy[selectedModelName];
        }
        collectAdvancedParameters({ config_overwrite: copy });
        isEditingRef.current = false;
        return;
      }
      try {
        const parsedValue = JSON.parse(value);
        const copy = { ...advancedParameters.config_overwrite };
        copy[selectedModelName] = parsedValue;

        collectAdvancedParameters({ config_overwrite: copy });
        isEditingRef.current = false;
      } catch (e) {
        if (isMountedRef.current) {
          setJSONParseError(e.message);
        }
      }
    }, 1000),
    [
      advancedParameters.config_overwrite,
      selectedModelName,
      collectAdvancedParameters,
    ]
  );

  useEffect(() => {
    return () => {
      updateConfigOverwrite.cancel();
    };
  }, [updateConfigOverwrite]);

  return <>{renderBody()}</>;
};
